<template>
  <div>
    <content-placeholder v-if="fetching">
      <el-card>
        <p class="text text-10 text-h-20"></p>
        <p class="text text-full"></p>
        <p class="text text-50"></p>
      </el-card>
    </content-placeholder>

    <div v-else>
      <el-card>
        <div slot="header">
          {{date.format('dddd, MMMM Do')}}
        </div>
        <div slot="actions" v-if="showManualEntry">
          <el-button @click="handleEdit({started_at: date, stopped_at:  date})" size="mini">
            Manual Entry
          </el-button>
        </div>
        <div v-if="totalEntries">
          <el-table
            class="depth"
            :border="true"
            :data="timeLogs"
            style="width: 100%;">
            <el-table-column label="Mods" align="center" width="60">
              <template slot-scope="scope">
                <span v-if="scope.row.versions_count > 0">
                  <el-tooltip class="item" effect="dark" content="# of Modifications" placement="top">
                    <el-badge :value="scope.row.versions_count" class="item"></el-badge>
                  </el-tooltip>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Type" align="center" width="60">
              <template slot-scope="scope">
                <span v-if="scope.row.custom === true">
                  <el-tooltip class="item" effect="dark" content="Manual entry" placement="top">
                    <i class="fas fa-user-edit fa-lg text--danger"></i>
                  </el-tooltip>
                </span>
                <span v-else>
                  <el-tooltip class="item" effect="dark" content="Automatic entry" placement="top">
                    <i class="fas fa-stopwatch fa-lg text--success"></i>
                  </el-tooltip>
                </span>
              </template>
            </el-table-column>

            <el-table-column label="Start" align="right" width="110">
              <template slot-scope="scope">
                {{ scope.row.started_at | formatTime('hh:mm:ss a') }}
              </template>
            </el-table-column>
            <el-table-column label="End" align="right" width="110">
              <template slot-scope="scope">
                {{ scope.row.stopped_at | formatTime('hh:mm:ss a') }}
              </template>
            </el-table-column>
            <el-table-column label="Length" align="right">
              <template slot-scope="scope">
                {{ duration(scope.row) | formatDiff }}
              </template>
            </el-table-column>
            <el-table-column label="Overlap" align="right">
              <template slot-scope="scope">
                {{ overlap(scope.row) }}
              </template>
            </el-table-column>
            <el-table-column label="Category" align="center">
              <template slot-scope="scope">
                {{ scope.row.category.name }}
              </template>
            </el-table-column>
            <el-table-column label="Notes" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.note">
                  <el-tooltip class="item" effect="dark" :content="scope.row.note" placement="top"><i class="fal fa-comment-alt-dots"></i></el-tooltip>
                </span>
              </template>
            </el-table-column>
            <el-table-column label="" width="120" align="center" v-if="showEdit">
              <template slot-scope="scope">
                <el-button @click="handleEdit(scope.row)" type="success" size="mini"><i class="fas fa-pencil"></i></el-button>
                <el-button @click="handleDelete(scope.row)" type="danger" size="mini"><i class="fas fa-trash-alt"></i></el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div v-else>
          <el-alert type="info" :closable="false" title="No log entries!" center></el-alert>
        </div>
        <div slot="footer">
          <el-row :gutter="20">
            <el-col :span="8">
              <b>Paid Time:</b> {{ totalPaidTime | formatDiff }}
            </el-col>
            <el-col :span="8">
              <b>Unpaid Time:</b> {{ totalUnpaidTime | formatDiff }}
            </el-col>
            <el-col :span="8" align="right">
              <b>Entries:</b> {{ totalEntries }}
            </el-col>
          </el-row>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script>
  import moment from 'moment';
  import { mapGetters } from 'vuex';

  import { showSuccessMessage, showErrorMessage } from '@/ui/message';
  import durationMixin from '@/store/modules/timeLog/mixins/duration';

  export default {
    name: 'TimeLogs',
    props: {
      date: {
        required: true,
      },
      userId: {
        required: false,
      },
      showEdit: {
        type: Boolean,
        default: false,
        required: false,
      },
      showManualEntry: {
        type: Boolean,
        default: true,
        required: false,
      },
    },
    mixins: [durationMixin],
    created() {
      this.fetchTimeLogs();
    },
    computed: {
      ...mapGetters('entities/timeLogs', ['fetching']),
      ...mapGetters('entities/users', ['currentUser']),

      timeLogs() {
        if (!this.currentUser && !this.userId) {
          return [];
        }

        let currentUserId = this.currentUser ? this.currentUser.id : null;

        if (this.userId) {
          currentUserId = this.userId;
        }
        const day = this.date.startOf('day').format('dddd, MMMM Do YYYY');

        return this.$store.getters['entities/timeLogs/query']()
          .with('category')
          .where(record => record.stopped_at !== null)
          .where(record => record.user_id === currentUserId)
          .where('deleted', false)
          .where(record => moment(record.started_at).format('dddd, MMMM Do YYYY') === day)
          .orderBy('started_at', 'asc')
          .get();
      },
      today() {
        return moment();
      },
      totalPaidTime() {
        return this.timeLogsDuration(this.timeLogs.filter(timeLog => timeLog.category.name !== 'Unpaid'));
      },
      totalUnpaidTime() {
        return this.timeLogsDuration(this.timeLogs.filter(timeLog => timeLog.category.name === 'Unpaid'));
      },
      totalEntries() {
        return this.timeLogs ? this.timeLogs.length : 0;
      },
    },
    methods: {
      fetchTimeLogs() {
        if (!this.userId && !this.currentUser) {
          return Promise.resolve({ status: false, data: null });
        }

        const params = {
          mode: 'day',
          day: this.date.format('dddd, MMMM Do YYYY'),
          year: moment(this.date).year(),
          user_id: this.userId,
        };

        return this.$store.dispatch('entities/timeLogs/fetchTimeLogs', params);
      },
      duration(timeLog) {
        return this.timeLogsDuration([timeLog]);
      },
      overlap(timeLog) {
        const stoppedAt = moment(timeLog.stopped_at);
        if (stoppedAt.isAfter(this.today, 'day')) {
          return '(Ends next day)';
        }
        return '';
      },
      handleEdit(timeLog) {
        this.$store.dispatch('entities/timeLogs/openFormModal', timeLog.id);
      },
      handleDelete(timeLog) {
        this.$confirm('Do you really want to delete this time log?', 'Warning', {
          confirmButtonText: 'OK',
          cancelButtonText: 'Cancel',
          title: 'Confirm',
        }).then(() => {
          this.$store.dispatch('entities/timeLogs/deleteTimeLog', timeLog.id)
            .then((response) => {
              if (response.status) {
                showSuccessMessage('Time log has been deleted successfully');
              } else {
                showErrorMessage(response.error);
              }
            });
        });
      },
      handleAdd(timeLog) {
        this.$store.dispatch('entities/timeLogs/openFormModal', timeLog);
      },
    },
    watch: {
      date() {
        this.fetchTimeLogs();
      },
    },
  };
</script>

<style lang="scss" scoped>
  .el-button{
    margin-left: 0 !important;
  }
  .footer{
    margin-top: 10px;
    font-weight: bold;
  }
</style>



// WEBPACK FOOTER //
// src/components/timeLog/TimeLogs.vue